/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/emailTemplatePreview.scss';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {nullify, sanitizeHtml, WithBackground} from '@dvrd/dvr-controls';

import {BluemateButton} from '../../../controls';

import classNames from 'classnames';

import {ActivateHandle} from '../../../../util/interfaces';

function EmailTemplatePreview(props: object, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    // Either HTML string or URL
    const [preview, setPreview] = useState('');
    const [isHTML, setIsHTML] = useState(false);
    const [isLegacy, setIsLegacy] = useState(false);
    const [bcc, setBcc] = useState<string | null>(null);
    const [subject, setSubject] = useState<string | null>(null);

    function onActivate(
        previewName: string,
        isHTML: boolean = false,
        isLegacy: boolean = false,
        bcc: string | null = null,
        subject: string | null = null
    ) {
        setPreview(previewName);
        setIsHTML(isHTML);
        setIsLegacy(isLegacy);
        setBcc(nullify(bcc));
        setSubject(nullify(subject));
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setIsHTML(false);
        setIsLegacy(false);
        setBcc(null);
        setSubject(null);
        setPreview('');
    }

    function processBody(): string {
        let html = sanitizeHtml(preview);
        html = html.replace(/\{links}/g, `<em>${t('email.template.preview.reviewLinks', 'Review Links')}</em>`);
        return html;
    }

    function renderSettings() {
        if (!bcc && !subject) return null;
        return (
            <div className='mail-settings'>
                {bcc !== null && renderSetting(t('email.template.preview.bcc', 'BCC'), bcc)}
                {subject !== null && renderSetting(t('email.template.preview.subject', 'Onderwerp'), subject)}
            </div>
        );
    }

    function renderSetting(label: string, value: string) {
        return (
            <>
                <label className='key'>{label}</label>
                <label>{value}</label>
            </>
        );
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    if (!preview) return null;
    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='email-template-preview default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('email.template.preview.title', 'Preview')}</label>
                </div>
                {renderSettings()}
                <div className={classNames('body-container', isLegacy && 'legacy', isHTML && 'html')}>
                    {isHTML ? (
                        <div dangerouslySetInnerHTML={{__html: processBody()}} />
                    ) : (
                        <img src={preview} className='preview' alt='' />
                    )}
                </div>
                <div className='footer-container'>
                    <BluemateButton onClick={onClose} label={t('email.template.preview.done', 'Klaar')} secondary />
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, object>(EmailTemplatePreview);
